import {
  APP_STORE_URL,
  PLAY_STORE_PACKAGE,
  PLAY_STORE_URL,
} from './constants';
import type { Platform } from './platform';

export type LaunchOutcome =
  | 'launched-universal-link'
  | 'launched-android-intent'
  | 'redirected-to-external-browser'
  | 'redirected-to-store'
  | 'desktop-noop';

/**
 * 현재 페이지 URL 을 기반으로 앱이 처리해야 할 Universal Link target 을 만든다.
 *
 * - 앱의 `getLinkURLInfo` 는 `https://applink.woorien.com/<segment>[/<segment>]?...` 형태를
 *   기대하므로, 우리 페이지가 그대로 진입한 URL 을 deep link target 으로 재사용하면
 *   앱이 알고 있는 16개 path 를 모두 자동 지원한다.
 * - 다만 dev/preview 환경 (`BASE_URL=/preview/`) 에서는 path 앞에 `/preview` 가 붙는데,
 *   이 prefix 를 제거해야 앱이 인식한다. prod (`BASE_URL=/`) 에서는 strip 자체가 no-op.
 * - tracking_id 등 query 는 보존된 채 그대로 넘어간다 (앱 코드가 알지 못하는 키는 무시).
 */
export function buildDeepLinkTarget(): string {
  if (typeof window === 'undefined') return '';
  const url = new URL(window.location.href);
  const basePath = import.meta.env.BASE_URL ?? '/';
  url.pathname = stripBasePath(url.pathname, basePath);
  return url.toString();
}

function stripBasePath(pathname: string, basePath: string): string {
  if (!basePath || basePath === '/') return pathname;
  if (pathname === basePath || `${pathname}/` === basePath) return '/';
  if (pathname.startsWith(basePath)) {
    const rest = pathname.slice(basePath.length);
    return `/${rest.replace(/^\/+/, '')}`;
  }
  return pathname;
}

function safeNavigate(url: string): void {
  if (typeof window === 'undefined') return;
  window.location.href = url;
}

/**
 * 메인 CTA: 가능한 모든 우회 경로를 시도하여 우리엔 앱을 연다.
 *
 * 모든 분기에서 deep link target = 현재 페이지 URL (preview prefix strip 후) 로 동일.
 * 이렇게 해야 앱이 `getLinkURLInfo` 로 path 를 인식하여 정확한 화면으로 라우팅한다.
 *
 * - 데스크탑: noop (QR 안내)
 * - 카카오톡 인앱: kakaotalk://web/openExternal?url=<target>
 *     -> 외부 브라우저가 target 으로 진입 -> OS 가 Universal Link/App Link 자동 트리거
 * - 기타 in-app iOS: x-safari-https://<target without scheme> -> Safari
 * - 기타 in-app Android: intent://<target> -> 외부 Chrome
 * - 외부 일반 Android: intent:// + Play Store fallback (referrer=target)
 * - 외부 일반 iOS: location.href = target (Safari 가 Universal Link 시도) +
 *                  1.5s 안에 여전히 visible 이면 미설치로 간주 -> App Store
 */
export function openApp(platform: Platform): LaunchOutcome {
  if (typeof window === 'undefined') return 'desktop-noop';
  if (platform.desktop) return 'desktop-noop';

  const target = buildDeepLinkTarget();
  const targetWithoutScheme = target.replace(/^https?:\/\//, '');

  if (platform.inAppBrowser === 'kakaotalk') {
    safeNavigate(
      `kakaotalk://web/openExternal?url=${encodeURIComponent(target)}`,
    );
    return 'redirected-to-external-browser';
  }

  if (platform.inAppBrowser) {
    if (platform.iOS) {
      safeNavigate(`x-safari-https://${targetWithoutScheme}`);
      return 'redirected-to-external-browser';
    }
    safeNavigate(
      `intent://${targetWithoutScheme}#Intent;scheme=https;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;end`,
    );
    return 'redirected-to-external-browser';
  }

  if (platform.android) {
    const fallbackUrl = `${PLAY_STORE_URL}&referrer=${encodeURIComponent(target)}`;
    safeNavigate(
      `intent://${targetWithoutScheme}#Intent;scheme=https;package=${PLAY_STORE_PACKAGE};S.browser_fallback_url=${encodeURIComponent(
        fallbackUrl,
      )};end`,
    );
    return 'launched-android-intent';
  }

  if (platform.iOS) {
    const startedAt = Date.now();
    safeNavigate(target);
    window.setTimeout(() => {
      const elapsed = Date.now() - startedAt;
      if (document.visibilityState === 'visible' && elapsed < 2500) {
        safeNavigate(APP_STORE_URL);
      }
    }, 1500);
    return 'launched-universal-link';
  }

  return 'desktop-noop';
}

/**
 * 보조 CTA: 단순히 사용자 OS 에 맞는 스토어로 이동.
 * Android 는 Install Referrer 로 deep link target 을 함께 전달하여,
 * 향후 앱 측에서 InstallReferrerClient 를 연동하면 deferred deep link 도 가능.
 */
export function openInstall(platform: Platform): LaunchOutcome {
  if (typeof window === 'undefined') return 'desktop-noop';

  if (platform.android) {
    const target = buildDeepLinkTarget();
    safeNavigate(`${PLAY_STORE_URL}&referrer=${encodeURIComponent(target)}`);
    return 'redirected-to-store';
  }

  safeNavigate(APP_STORE_URL);
  return 'redirected-to-store';
}
